import React, {lazy, Suspense, useEffect, useContext} from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import { QueryParamProvider } from 'use-query-params';
import {
    // eslint-disable-next-line camelcase
    UNSAFE_NavigationContext,
    useNavigate,
    Navigate,
    Route,
    Routes,
    useLocation,
    // eslint-disable-next-line camelcase
    UNSAFE_DataRouterContext,
} from 'react-router';

import { useTrackPageViews } from 'src/services/analytics';
import { landlordAuthRoutes, landlordProtectedRoutes, LANDLORD_HOME_PATH } from 'src/routes';
import { ThemeProvider } from 'src/theme';

import LandlordLayout from 'src/components/layout/landlord/LandlordLayout';
import AuthenticatedContainer from 'src/components/authenticated-container';
const OnboardingPage = lazy(() => import('src/pages/landlord/onboard/OnboardingPage'));
const NotFoundPage = lazy(() => import('src/pages/not-found/NotFound'));
import TestErrorBoundaryPage from 'src/pages/test-error-boundary/TestErrorBoundaryPage';

const useSuspenseFallback = () => {
    const { pathname } = useLocation();

    if (pathname.startsWith('/landlord/inner')) {
        return <LandlordLayout isLoading={true} />;
    } else {
        return (
            <ThemeProvider>
                <LinearProgress />
            </ThemeProvider>
        );
    }
};

const ReactRouter7Adapter = ({ children } : any) => {
    // https://github.com/pbeshai/use-query-params/issues/295
    const { navigator } = useContext(UNSAFE_NavigationContext);
    const navigate = useNavigate();
    const router = useContext(UNSAFE_DataRouterContext)?.router;
    const location = useLocation();

    const adapter = {
        // eslint-disable-next-line @typescript-eslint/no-shadow
        replace(location: any) {
            navigate(location.search || '?', {
                replace: true,
                state: location.state,
            });
        },
        // eslint-disable-next-line @typescript-eslint/no-shadow
        push(location: any) {
            navigate(location.search || '?', {
                replace: false,
                state: location.state,
            });
        },
        get location() {
            return router?.state?.location ?? (navigator as any)?.location ?? location;
        },
    };

    return children(adapter);
};

export default function App() {
    const { pathname } = useLocation();
    const fallback = useSuspenseFallback();

    // Scroll to top on route change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    useTrackPageViews();

    return (
        <Suspense fallback={fallback}>
            <QueryParamProvider adapter={ReactRouter7Adapter}>
                <Routes>
                    <Route path="/" element={<Navigate to={LANDLORD_HOME_PATH} />} />
                    <Route path="/landlord" element={<Navigate to={LANDLORD_HOME_PATH} />} />
                    <Route path="/landlord/inner" element={<Navigate to={LANDLORD_HOME_PATH} />} />

                    {/* TODO: Put all landlord auth routes under `/landlord/auth` */}
                    {...landlordAuthRoutes}

                    <Route path="/landlord/onboard/*" element={<OnboardingPage />} />

                    <Route path="/landlord/inner" element={<AuthenticatedContainer />}>
                        {...landlordProtectedRoutes}
                    </Route>

                    <Route path="/test-error-boundary" element={<TestErrorBoundaryPage />} />

                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </QueryParamProvider>
        </Suspense>
    );
}
